import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  CardContent,
  CircularProgress,
  Divider,
  FormHelperText,
  Input,
  Snackbar,
  Typography,
} from '@mui/joy';
import { Paper, Typography as MuiTypography } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import PermissionGate from '../../../main/components/PermissionGate';
import { PERMISSIONS } from '../../../main/context/AuthContext';
import { usePermissions } from '../../../main/hooks/usePermissions';
import { getAllSettings, updateSettingById } from '../../../main/API/settings';
import { translateAxiosErrorMessage } from '../../../main/utils/masterApiError';

const BARCODE_PREFIX_RE = /^[A-Z0-9]{0,8}$/;
const SETTING_NAME = 'BARCODE_PREFIX';

/**
 * Standalone editor for the `BARCODE_PREFIX` setting (used by new shipment
 * piece barcodes). Mounted in `ShipmentsConfigurationScreen` and uses the
 * `/settings/setting/<id>/` endpoint via the existing helpers. Renders a
 * Paper "card" so the page only needs to provide the standard settings
 * shell (theme, container, page heading) — matches the layout of the other
 * settings pages such as `WhatsAppMessagesScreen`.
 *
 * UI rules:
 *  - Allowed chars: A–Z and 0–9. Up to 8 characters. Empty allowed.
 *  - The backend normalizes (uppercases) the value; invalid values fall back
 *    to empty. We mirror that client-side for instant feedback.
 *  - We surface a notice that existing barcodes will NOT change.
 */
export default function BarcodePrefixSetting() {
  const { t } = useTranslation('ui');
  const { hasPermission } = usePermissions();

  const canList = hasPermission(PERMISSIONS.SETTINGS_LIST);
  const canUpdate = hasPermission(PERMISSIONS.SETTINGS_UPDATE_DETAIL);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [setting, setSetting] = useState(null);
  const [draft, setDraft] = useState('');
  const [snack, setSnack] = useState({ open: false, message: '', color: 'success' });
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!canList) return;
    setLoading(true);
    try {
      const settings = await getAllSettings();
      const list = Array.isArray(settings) ? settings : [];
      const row = list.find((s) => (s && s.name) === SETTING_NAME);
      setSetting(row || null);
      setDraft(row && typeof row.value === 'string' ? row.value : '');
    } catch (e) {
      setError(translateAxiosErrorMessage(e, t) || t('settingsPages.common.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [canList, t]);

  useEffect(() => { load(); }, [load]);

  const normalized = useMemo(() => String(draft || '').toUpperCase(), [draft]);
  const isValid = BARCODE_PREFIX_RE.test(normalized);
  const hasChanges = setting && normalized !== (setting.value || '');

  const handleSave = useCallback(async () => {
    if (!canUpdate || !setting || !isValid) return;
    setSaving(true);
    setError(null);
    try {
      await updateSettingById(setting.id, {
        name: setting.name,
        value: normalized,
        description: setting.description || '',
      });
      setSetting({ ...setting, value: normalized });
      setSnack({ open: true, color: 'success', message: t('barcodePrefix.savedSuccess') });
    } catch (e) {
      setSnack({
        open: true,
        color: 'danger',
        message: translateAxiosErrorMessage(e, t) || t('barcodePrefix.saveFailed'),
      });
    } finally {
      setSaving(false);
    }
  }, [canUpdate, setting, isValid, normalized, t]);

  return (
    <PermissionGate permissions={[PERMISSIONS.SETTINGS_LIST]}>
      <Paper
        elevation={0}
        sx={{
          mb: 4,
          borderRadius: 3,
          border: '1px solid #E5E7EB',
          background: 'linear-gradient(135deg, #FFFFFF 0%, #F9FAFB 100%)',
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: '#EDE9FE',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 1.5,
                }}
              >
                <QrCodeScannerIcon sx={{ color: '#5f09c2', fontSize: 24 }} />
              </Box>
              <Box>
                <MuiTypography variant="h6" sx={{ fontWeight: 600, color: '#1F2937' }}>
                  {t('barcodePrefix.title')}
                </MuiTypography>
                <MuiTypography variant="body2" color="#6B7280">
                  {t('barcodePrefix.subtitle')}
                </MuiTypography>
              </Box>
            </Box>
            <Divider />
          </Box>

          <Alert color="warning" variant="soft" sx={{ mb: 2 }}>
            {t('barcodePrefix.noticeExistingImmutable')}
          </Alert>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size="sm" />
            </Box>
          ) : !setting ? (
            <Alert color="neutral" variant="soft">
              {t('settingsPages.common.loadFailed')}
            </Alert>
          ) : (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                border: '1px solid #F3F4F6',
                background: '#FFFFFF',
              }}
            >
              <Box sx={{ mb: 1.5 }}>
                <Typography level="title-sm" sx={{ fontWeight: 600, color: '#1F2937', mb: 0.5 }}>
                  {t('barcodePrefix.title')}
                </Typography>
                <Typography level="body-sm" sx={{ color: '#6B7280' }}>
                  {t('barcodePrefix.hint')}
                </Typography>
              </Box>
              <Input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={t('barcodePrefix.placeholder')}
                color={isValid ? 'neutral' : 'danger'}
                slotProps={{ input: { maxLength: 8, style: { fontFamily: 'monospace', letterSpacing: 1 } } }}
                disabled={saving || !canUpdate}
              />
              {!isValid && (
                <FormHelperText sx={{ mt: 0.5, color: 'danger.500' }}>
                  {t('barcodePrefix.hint')}
                </FormHelperText>
              )}
              {error && (
                <Alert color="danger" variant="soft" sx={{ mt: 1 }}>
                  {error}
                </Alert>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <PermissionGate permissions={[PERMISSIONS.SETTINGS_UPDATE_DETAIL]}>
                  <Button
                    type="button"
                    variant="solid"
                    color="primary"
                    size="sm"
                    startDecorator={saving ? <CircularProgress sx={{ width: 16, height: 16 }} /> : <SaveIcon />}
                    onClick={handleSave}
                    disabled={!canUpdate || !isValid || !hasChanges || saving}
                    sx={{ minWidth: 120 }}
                  >
                    {saving ? t('settingsPages.common.saving') : t('actions.save')}
                  </Button>
                </PermissionGate>
              </Box>
            </Paper>
          )}
        </CardContent>
      </Paper>

      <Snackbar
        autoHideDuration={3000}
        open={snack.open}
        variant="soft"
        color={snack.color}
        onClose={(_, reason) => { if (reason !== 'clickaway') setSnack((s) => ({ ...s, open: false })); }}
      >
        {snack.message}
      </Snackbar>
    </PermissionGate>
  );
}
